import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { Lock, LogIn, UserPlus, X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

interface AuthGuardOptions {
  silent?: boolean;
}

export function useRequireAuth(options?: AuthGuardOptions) {
  const { isAuthenticated, isGuest, user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const requireAuth = useCallback((action?: () => void) => {
    // Guest users or non-authenticated users must sign in
    if (!isAuthenticated || isGuest) {
      if (!options?.silent) {
        setShowModal(true);
      }
      return false;
    }
    // Real authenticated user — allow
    action?.();
    return true;
  }, [isAuthenticated, isGuest, options?.silent]);

  const AuthModal = useCallback(() => {
    const [mode, setMode] = useState<'login' | 'register'>('login');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { login, register, isLoading, error } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (mode === 'login') {
        const ok = await login(username, password);
        if (ok) {
          setShowModal(false);
          toast.success('Signed in successfully');
        }
      } else {
        if (password.length < 4) {
          toast.error('Password must be at least 4 characters');
          return;
        }
        const ok = await register(username, password);
        if (ok) {
          const loginOk = await login(username, password);
          if (loginOk) {
            setShowModal(false);
            toast.success('Account created and signed in');
          }
        }
      }
    };

    return (
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-700 overflow-hidden"
              style={{ background: 'var(--bg-surface)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Sign In Required</h3>
                    <p className="text-xs text-slate-500">Database operations require authentication</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                {/* Mode toggle */}
                <div className="flex rounded-lg border border-slate-700 overflow-hidden">
                  <button
                    onClick={() => setMode('login')}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      mode === 'login' ? 'bg-rb-brand text-slate-200' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setMode('register')}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      mode === 'register' ? 'bg-rb-brand text-slate-200' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Register
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder="Enter username"
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-800/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-rb-brand/30 focus:border-rb-brand"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder={mode === 'register' ? 'Min 4 characters' : 'Enter password'}
                        className="w-full px-3 py-2.5 pr-10 rounded-lg border border-slate-700 bg-slate-800/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-rb-brand/30 focus:border-rb-brand"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {error && (
                    <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading || !username.trim() || !password.trim()}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] disabled:opacity-40 transition-all"
                  >
                    {mode === 'login' ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                    {isLoading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                </form>

                {/* Demo credentials hint */}
                <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
                  <p className="text-[10px] text-slate-500">
                    <span className="text-slate-400 font-bold">Demo:</span> username <span className="text-rb-brand">tasman</span> / password <span className="text-rb-brand">tasman</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [showModal]);

  return { requireAuth, AuthModal, showModal, setShowModal };
}
